<template>
  <surface-editor-element-toolbar-font-select v-model:selectedFont="selectedFont" />
  <core-tooltip text="Font size">
    <u-input v-model="fontSize" type="number" min="6" step="1" class="w-20" :ui="{rounded: 'rounded-md'}" size="sm" />
  </core-tooltip>

  <core-tooltip text="Text color">
    <u-button
      color="charcoal"
      variant="ghost"
      icon="i-mdi-format-color-text"
      @click="openColorPanel"
      class="relative items-center dark:text-white"
    >
      <div
        class="w-6 h-1 absolute bottom-1 left-1/2 -translate-x-1/2 rounded-full shadow"
        :style="{backgroundColor: content.color}"
      />
    </u-button>
  </core-tooltip>

  <surface-editor-core-toolbar-divider />

  <core-tooltip text="Bold">
    <u-button
      :variant="isBold ? 'solid' : 'ghost'"
      :color="isBold ? 'primary' : 'charcoal'"
      icon="i-ri-bold"
      class="dark:text-white"
      @click="toggle('bold')"
    />
  </core-tooltip>
  <core-tooltip text="Italic">
    <u-button
      icon="i-ri-italic"
      :variant="isItalic ? 'solid' : 'ghost'"
      :color="isItalic ? 'primary' : 'charcoal'"
      class="dark:text-white"
      @click="toggle('italic')"
    />
  </core-tooltip>
  <core-tooltip text="Underline">
    <u-button
      icon="i-ri-underline"
      :variant="isUnderline ? 'solid' : 'ghost'"
      :color="isUnderline ? 'primary' : 'charcoal'"
      class="dark:text-white"
      @click="toggle('underline')"
    />
  </core-tooltip>
  <core-tooltip text="Strikethrough">
    <u-button
      icon="i-ri-strikethrough"
      :variant="isStrike ? 'solid' : 'ghost'"
      :color="isStrike ? 'primary' : 'charcoal'"
      class="dark:text-white"
      @click="toggle('strikethrough')"
    />
  </core-tooltip>

  <surface-editor-core-toolbar-divider />

  <core-tooltip text="Align left">
    <u-button
      icon="i-ri-align-left"
      :variant="content.align === 'left' ? 'solid' : 'ghost'"
      :color="content.align === 'left' ? 'primary' : 'charcoal'"
      class="dark:text-white"
      @click="setAlign('left')"
    />
  </core-tooltip>
  <core-tooltip text="Align center">
    <u-button
      icon="i-ri-align-center"
      :variant="content.align === 'center' ? 'solid' : 'ghost'"
      :color="content.align === 'center' ? 'primary' : 'charcoal'"
      class="dark:text-white"
      @click="setAlign('center')"
    />
  </core-tooltip>
  <core-tooltip text="Align right">
    <u-button
      icon="i-ri-align-right"
      :variant="content.align === 'right' ? 'solid' : 'ghost'"
      :color="content.align === 'right' ? 'primary' : 'charcoal'"
      class="dark:text-white"
      @click="setAlign('right')"
    />
  </core-tooltip>
  <core-tooltip text="Justify">
    <u-button
      icon="i-ri-align-justify"
      :variant="content.align === 'justify' ? 'solid' : 'ghost'"
      :color="content.align === 'justify' ? 'primary' : 'charcoal'"
      class="dark:text-white"
      @click="setAlign('justify')"
    />
  </core-tooltip>

  <surface-editor-core-toolbar-divider />

  <u-popover>
    <core-tooltip text="Advanced settings">
      <u-button
        color="charcoal"
        variant="ghost"
        icon="i-radix-icons-letter-spacing"
        class="dark:text-white"
      />
    </core-tooltip>
    <template #panel>
      <div class="p-4 space-y-4">
        <surface-editor-core-slider-input
          v-model="letterSpacing"
          :min="SURFACE_EDITOR_LETTER_SPACING_MIN"
          :max="SURFACE_EDITOR_LETTER_SPACING_MAX"
          :step="SURFACE_EDITOR_LETTER_SPACING_STEP"
          @drag-start="surfaceEditorStore.setSliderDragging(true)"
          @drag-end="surfaceEditorStore.setSliderDragging(false)"
          @commit-updates="surfaceEditorStore.commitUndo()"
        >
          <template #label>Letter Spacing</template>
        </surface-editor-core-slider-input>

        <surface-editor-core-slider-input
          v-model="lineSpacing"
          :min="SURFACE_EDITOR_LINE_SPACING_MIN"
          :max="SURFACE_EDITOR_LINE_SPACING_MAX"
          :step="SURFACE_EDITOR_LINE_SPACING_STEP"
          @drag-start="surfaceEditorStore.setSliderDragging(true)"
          @drag-end="surfaceEditorStore.setSliderDragging(false)"
          @commit-updates="surfaceEditorStore.commitUndo()"
        >
          <template #label>Line Spacing</template>
        </surface-editor-core-slider-input>
      </div>
    </template>
  </u-popover>
</template>

<script setup>
  import {storeToRefs} from 'pinia';

  const surfaceEditorStore = useSurfaceEditorStore();
  const {selectedElement} = storeToRefs(surfaceEditorStore);

  const surfaceEditorUtils = useSurfaceEditorUtils();

  const content = computed(() => selectedElement.value.content);

  const selectedFont = computed({
    get() {
      return content.value.font_family;
    },
    set(fontFamily) {
      surfaceEditorStore.updateElement({
        element: selectedElement.value,
        updates: {
          content: {
            font_family: fontFamily
          }
        }
      });
    }
  });

  const fontSize = computed({
    get() {
      return Math.round(surfaceEditorUtils.fontPixelsToPoints({pixels: content.value.font_size * content.value.scale}));
    },
    set(fontSize) {
      surfaceEditorStore.updateElement({
        element: selectedElement.value,
        updates: {
          content: {
            font_size: Math.round(surfaceEditorUtils.fontPointsToPixels({points: fontSize})),
            scale: 1
          }
        }
      });
    }
  });

  const isBold = computed(() => content.value.bold === true);
  const isItalic = computed(() => content.value.italic === true);
  const isUnderline = computed(() => content.value.underline === true);
  const isStrike = computed(() => content.value.strikethrough === true);

  const letterSpacing = computed({
    get() {
      return Math.round((content.value.letter_spacing || 0) * 1000);
    },
    set(letterSpacing) {
      const spacing = Math.max(SURFACE_EDITOR_LETTER_SPACING_MIN, Math.min(SURFACE_EDITOR_LETTER_SPACING_MAX, Number(letterSpacing))) / 1000;
      surfaceEditorStore.updateElement({
        element: selectedElement.value,
        updates: {
          content: {
            letter_spacing: spacing
          }
        },
        commit: false
      });
    }
  });

  const lineSpacing = computed({
    get() {
      return content.value.line_height;
    },
    set(lineHeight) {
      const spacing = Math.max(SURFACE_EDITOR_LINE_SPACING_MIN, Math.min(SURFACE_EDITOR_LINE_SPACING_MAX, Number(lineHeight)));
      surfaceEditorStore.updateElement({
        element: selectedElement.value,
        updates: {
          content: {
            line_height: spacing
          }
        },
        commit: false
      });
    }
  });

  function toggle(prop) {
    surfaceEditorStore.updateElement({
      element: selectedElement.value,
      updates: {
        content: {
          [prop]: !content.value[prop]
        }
      }
    });
  }

  function openColorPanel() {
    surfaceEditorStore.openContextualPanel({
      id: 'color',
      props: {
        current: computed(() => ({
          color: content.value.color,
          type: SURFACE_EDITOR_COLOR_TYPES.solid.key
        })),
        onSelect: setColor,
        mode: SURFACE_EDITOR_COLOR_PANEL_MODES.text
      }
    });
  }

  function setColor(selected) {
    surfaceEditorStore.updateElement({
      element: selectedElement.value,
      updates: {
        content: {
          color: selected.color
        }
      }
    });
  }

  function setAlign(align) {
    surfaceEditorStore.updateElement({
      element: selectedElement.value,
      updates: {
        content: {align}
      }
    });
  }

</script>
